import React, { Fragment } from "react";

import validate from "validate.js";
import isFunction from "is-function";

import { translate } from "react-i18next";

import { withFormik } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import * as R from "ramda";

import { graphql } from "react-apollo";
import gql from "graphql-tag";

import {
  compose,
  withProps,
  withState,
  lifecycle,
  defaultProps,
} from "recompose";

import { Box, Flex, Text, Link } from "src/components";

import { shevy } from "src/styles";

import AmountInput from "./AmountInput";
import Button from "./Button";
import CheckboxInput from "./CheckboxInput";
import DateInput from "./DateInput";
import DropdownInput from "./DropdownInput";
import OptionButtons from "./OptionButtons";
import OptionRadio from "./OptionRadio";
import Form from "./Form";
import FormErrors from "./FormErrors";
import Input from "./Input";
import TextArea from "./TextArea";

const fieldTypes = {
  currency: AmountInput,
  date: DateInput,
  dropdown: DropdownInput,
  textarea: TextArea,
  optionButtons: OptionButtons,
  optionRadio: OptionRadio,
  checkbox: CheckboxInput,
};

const componentShowsInlineError = FieldComponent =>
  !FieldComponent.suppressInlineError;

const assocPathIfEmpty = (path, val) => obj => {
  if (!R.isNil(R.path(path, obj))) {
    return obj;
  }

  return R.assocPath(path, val, obj);
};

export const Field = ({ field, ...props }) => {
  if (!field) {
    return null;
  }

  if (isFunction(field)) {
    return <Field field={field(props.values || {})} {...props} />;
  }

  if (Array.isArray(field)) {
    return (
      <Flex
        mx={-2}
        flexDirection="row"
        flex="1 0 auto"
        align="flex-start"
        style={{ flexWrap: "wrap" }}
      >
        <DynamicFields
          fields={R.map(
            R.pipe(
              assocPathIfEmpty(["displayProps", "mx"], 2),
              assocPathIfEmpty(["displayProps", "minWidth"], 250)
            ),
            field
          )}
          {...props}
        />
      </Flex>
    );
  }

  if (field.type === "section") {
    return (
      <Flex flexDirection="column" mb={2} {...field.style}>
        {field.title ? (
          <Text
            fontWeight="bold"
            style={shevy.h4}
            color="darkText"
            textTransform="uppercase"
            borderWidth={1}
            borderColor="divider"
            borderBottom={true}
            pb={2}
            mb={2}
          >
            {field.title}
          </Text>
        ) : null}

        {field.renderBelowSectionTitle ? field.renderBelowSectionTitle() : null}

        <DynamicFields fields={field.fields} {...props} />
      </Flex>
    );
  }


  if (field.type === "content") {
    return (
      <Flex flexDirection="column" mb={2} {...field.style}>
        {field.title ? (
          <Text
            fontWeight="bold"
            style={shevy.h5}
            color="darkText"
            textTransform="uppercase"
            borderWidth={1}
            borderColor="divider"
            borderBottom={true}
            pt={2}
            pb={2}
          >
            {field.title}
          </Text>
        ) : null}

        {field.render ? field.render() : null}
      </Flex>
    );
  }

  const FieldComponent =
    field.Component || R.propOr(Input, field.type, fieldTypes);

  const value = props.values[field.key];

  const touched = R.pathOr(false, ["touched", field.key], props);

  const error = R.pipe(
    R.path(["errors", field.key]),
    R.unless(R.isNil, R.head)
  )(props);

  const helpTextError =
    Boolean(
      error &&
      touched &&
      (value ||
        componentShowsInlineError(FieldComponent) ||
        (field.inputProps && field.inputProps.renderErrorBelow))
    ) && error;

  // Use the label as the fallback placeholder unless it's `labelTop`.
  const placeholder =
    field.placeholder ||
    (field.inputProps && field.inputProps.labelTop ? undefined : field.label);

  const fieldProps = {
    placeholder,
    label: field.label,
    name: field.key,
    value,
    onChange: props.handleChange,
    setValue: value => props.setFieldValue(field.key, value),
    onTouch: () =>
      props.setTouched({
        ...props.touched,
        [field.key]: true,
      }),
    onBlur: props.handleBlur,
    onFocus: props.handleFocus,
    id: props.id,
    required: field.required,
    error: touched && error,
    valid: touched && !error,
    language: props.i18n.language,
    ...field.inputProps,
  };

  return (
    <FieldComponent
      {...fieldProps}
      displayProps={field.displayProps}
      renderHelpText={({ containerProps } = {}) =>
        (helpTextError || field.helpText) && (
          <Flex flexDirection="column" {...containerProps}>
            {helpTextError && <Text color="alert">{helpTextError}</Text>}
            {field.helpText && <Text color="muted">{field.helpText}</Text>}
          </Flex>
        )
      }
    />
  );
};

export const DynamicFields = ({ fields, formKey, ...props }) => (
  <Fragment>
    {fields.map((fieldOrFn, index) => {
      const field = isFunction(fieldOrFn) ? fieldOrFn(props) : fieldOrFn;

      return field ? (
        <Field
          key={field.key || index}
          field={field}
          {...props}
          formKey={formKey}
          id={`${formKey}-${field.key || index}`}
        />
      ) : null;
    })}
  </Fragment>
);

export const SuccessMessageDisplay = ({ message }) => (
  <Flex flexDirection="column">
    <Text color="green" fontSize={5} fontWeight="bold" mb={3}>
      {message}
    </Text>
  </Flex>
);

const defaultRenderConfirmation = ({ message }) => (
  <SuccessMessageDisplay message={message} />
);

const defaultRenderForm = ({
  errors,
  handleSubmit,
  formKey,
  fields,
  renderButton,
  renderCancel,
  renderCaptcha,
  renderErrors,
  errorMessages,
  isValid,
  submittingLabel,
  t,
  ...props
}) => (
    <Box>
      <Form onSubmit={handleSubmit} id={formKey}>
        <DynamicFields
          fields={fields}
          formKey={formKey}
          errors={errors}
          {...props}
        />
        {renderCaptcha && (
          <Flex mb={2} justify="center">
            {renderCaptcha({ formKey })}
          </Flex>
        )}
        <Flex flexDirection="column">
          {renderErrors({ errors, errorMessages })}
          {renderButton({
            isSubmitting: props.isSubmitting,
            isValid,
            t,
            formKey,
            values: props.values,
          })}
          {renderCancel && renderCancel()}
        </Flex>

        {props.isSubmitting && submittingLabel ? (
          <Text color="muted" fontSize={3} mt={2} style={{ textAlign: "center" }}>
            {submittingLabel}
          </Text>
        ) : null}
      </Form>
    </Box>
  );

const DynamicForm = ({
  successMessage,
  captchaLoaded,
  needsCaptcha = true,
  renderConfirmation = defaultRenderConfirmation,
  renderForm = defaultRenderForm,
  renderButton,
  onCancelRoute,
  submitButtonTitle,
  ...props
}) =>
  successMessage
    ? renderConfirmation({ values: props.values, message: successMessage })
    : renderForm({
      renderButton: renderButton
        ? ({ isSubmitting, isValid, formKey, t, values }) => {
          return renderButton({
            isSubmitting,
            isValid,
            formKey,
            t,
            values,
            resetForm: props.resetForm,
          });
        }
        : ({ isSubmitting, isValid, formKey, t }) => (
          <Button
            disabled={isSubmitting || !isValid}
            title={submitButtonTitle || t("button.submit")}
            id={`${formKey}-submit`}
          />
        ),
      renderCancel: () => {
        return onCancelRoute ? (
          <Link
            my={1}
            textDecoration="underline"
            color="green"
            style={{ cursor: "pointer" }}
            align="center"
            to={onCancelRoute}
          >
            Cancel
          </Link>
        ) : null;
      },
      ...props,
      renderErrors: ({
        errors,
        errorMessages,
        textOnly = false,
        ...props
      }) => {
        if (errorMessages || (errors && errors._form)) {
          return (
            <FormErrors
              errors={errorMessages || errors._form}
              textOnly={textOnly}
              {...props}
            />
          );
        }
        return null;
      },
      renderFormSuccessMessage: formSuccessMessage => (
        <Box bg="success" p={3} my={3} borderRadius={1}>
          <Text color="white" fontSize={4} fontWeight="bold">
            {formSuccessMessage}
          </Text>
        </Box>
      ),
      renderCaptcha:
        needsCaptcha &&
        captchaLoaded &&
        (({ formKey }) => (
          <ReCAPTCHA
            sitekey="6LfsAkEUAAAAAK-PUT3yB7XmYs5MZOSaY7H4XUss"
            onChange={recaptcha => {
              props.setFieldValue("recaptcha", recaptcha);
            }}
          />
        )),
    });

const getAllFieldsByKey = ({ values }) => {
  // A field can be an array, a function, a "section", or a plain object. This
  // function traverses each type and builds up a flattend list of fields.
  const extractFields = fields => {
    let combinedFields = [];

    if (fields) {
      R.forEach(field => {
        if (Array.isArray(field)) {
          combinedFields = [...combinedFields, ...extractFields(field)];
        } else if (isFunction(field)) {
          combinedFields = [
            ...combinedFields,
            ...extractFields([field({ values })]),
          ];
        } else if (field && field.type === "section") {
          combinedFields = [...combinedFields, ...extractFields(field.fields)];
        } else if (field) {
          combinedFields = [...combinedFields, field];
        }
      }, fields);
    }

    return combinedFields;
  };

  return R.pipe(
    extractFields,
    R.filter(R.prop("key")),
    R.indexBy(R.prop("key"))
  );
};
const mapPropsToValues = props => {
  const propsToValues =
    props.initialValues ||
    R.pipe(
      R.prop("fields"),
      getAllFieldsByKey({ values: {} }),
      R.map(R.propOr("", "defaultValue"))
    )(props);

  return propsToValues;
};

const addConstraints = ({ t }) => field => {
  if (field.required) {
    return {
      ...field,
      constraints: {
        presence: {
          allowEmpty: false,
          message: `^${t("forms.validations.required", {
            label: field.label || field.placeholder,
          })}`,
        },
        ...field.constraints,
      },
    };
  }

  return field;
};

export const fieldValidator = (
  values,
  { t, fields, needsCaptcha, customValidator }
) => {
  if (customValidator) {
    return customValidator(values, { t, fields, needsCaptcha });
  }

  const constraints = R.pipe(
    getAllFieldsByKey({ values }),
    R.map(addConstraints({ t })),
    R.filter(R.prop("constraints")),
    R.pluck("constraints"),
    R.when(
      R.always(needsCaptcha),
      R.assoc(
        "recaptcha",
        process.env.NODE_ENV !== "test" && {
          presence: { allowEmpty: false },
        }
      )
    )
  )(fields);

  return validate(values, constraints) || {};
};

const submitForm = gql`
  mutation submitForm($formKey: String!, $formData: String!) {
    submitForm(formKey: $formKey, formData: $formData) {
      message
      messageKey
    }
  }
`;

export const getSuccessMessage = (
  { message, messageKey },
  { t, defaultSuccessMessageKey }
) => {
  const successMessageKey = messageKey || defaultSuccessMessageKey;
  return message || (successMessageKey && t(successMessageKey));
};

export default compose(
  translate("translations"),
  withProps(({ fields, t }) => ({
    fields: fields({ t }),
  })),
  withState("successMessage", "setSuccessMessage"),
  withState("captchaLoaded", "setCaptchaLoaded", false),
  defaultProps({
    needsCaptcha: true,
  }),
  lifecycle({
    componentDidMount() {
      this.props.setCaptchaLoaded(true);
    },
  }),
  graphql(submitForm, {
    name: "postForm",
  }),
  withFormik({
    mapPropsToValues,
    validate: fieldValidator,
    isInitialValid: ({ initialValues, ...props }) => {
      if (props.isEditing) {
        return false;
      }
      const validationErrors = fieldValidator(initialValues, props);
      return R.isEmpty(validationErrors);
    },
    validateOnChange: true,
    handleSubmit: async (
      values,
      { props, setSubmitting, setErrors, setTouched, ...actions }
    ) => {
      if (props.onSubmit) {
        try {
          await props.onSubmit(values, {
            setErrors,
            setSubmitting,
            ...actions,
          });
        } catch (e) {
          const errors = Array.isArray(e) ? e : [e];

          setErrors({
            _form: errors,
          });
        } finally {
          try {
            setSubmitting(false);
          } catch (e) {
            // Let this fail silently if setSubmitting cannot be set
            // Because the form is unmounted
          }
        }

        return;
      }

      try {
        const valuesToPost = props.additionalValuesToPost
          ? {
            ...values,
            ...props.additionalValuesToPost,
          }
          : values;

        const { data } = await props.postForm({
          variables: {
            formKey: props.formKey,
            formData: JSON.stringify(valuesToPost),
          },
        });
        const message = getSuccessMessage(data.submitForm, props);
        if (message) {
          props.setSuccessMessage(message);
        }
      } catch (e) {
        console.warn("error", e);
        if (Array.isArray(e)) {
          setErrors({
            _form: e,
          });
        }
      } finally {
        setSubmitting(false);
      }
    },
  })
)(DynamicForm);



// WEBPACK FOOTER //
// ./src/components/Form/DynamicForm.js